import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Award, Heart, TrendingUp, Trophy, Target, Flame, Star, CheckCircle, BarChart3, Calendar, Brain, Settings, Lock } from 'lucide-react';
import { AuthContext } from '@/App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [testProgress, setTestProgress] = useState([]);
  const [categoryTierProgress, setCategoryTierProgress] = useState({});
  const [hubStats, setHubStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [jeopardyLifetimeScore, setJeopardyLifetimeScore] = useState(null);
  const [jeopardyLeaderboard, setJeopardyLeaderboard] = useState([]);
  const [publicationMembership, setPublicationMembership] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    try {
      const [dashRes, certRes, achRes, wishRes, testRes, hubRes, jeopardyScoreRes, jeopardyLeaderRes, membershipRes] = await Promise.all([
        axios.get(`${API}/student/dashboard`, { withCredentials: true }),
        axios.get(`${API}/student/certificates`, { withCredentials: true }),
        axios.get(`${API}/student/achievements`, { withCredentials: true }),
        axios.get(`${API}/student/wishlist`, { withCredentials: true }),
        axios.get(`${API}/student/test-progress`, { withCredentials: true }),
        axios.get(`${API}/student/hub-stats`, { withCredentials: true }),
        axios.get(`${API}/jeopardy-lifetime-score`, { withCredentials: true }),
        axios.get(`${API}/jeopardy-leaderboard`),
        axios.get(`${API}/publication-subscription/status`, { withCredentials: true })
      ]);
      
      setDashboard(dashRes.data);
      setCertificates(certRes.data);
      setAchievements(achRes.data);
      setWishlist(wishRes.data);
      // Handle both old and new API format
      setTestProgress(testRes.data.by_test || testRes.data);
      setCategoryTierProgress(testRes.data.by_category_tier || {});
      setHubStats(hubRes.data);
      setJeopardyLifetimeScore(jeopardyScoreRes.data);
      setJeopardyLeaderboard(jeopardyLeaderRes.data);
      setPublicationMembership(membershipRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
          confirm_password: passwordForm.confirmPassword
        },
        { withCredentials: true }
      );
      toast.success('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };


  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-16 min-h-screen" data-testid="student-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Welcome Header */}
          <div className="mb-12 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-5xl retro-title mb-4">Welcome back, {user?.name}!</h1>
              <p className="text-xl text-[#6B4423]">Continue your learning journey</p>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="retro-button-small flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <Settings className="w-4 h-4" />
              Settings
            </button>
          </div>

          {/* Student Hub - Comprehensive Statistics */}
          {hubStats && (
            <div className="mb-12">
              <h2 className="text-4xl retro-title mb-6 flex items-center gap-3">
                <Brain className="w-10 h-10 text-[#E8934D]" />
                Your Learning Analytics
              </h2>

              {/* Overall Performance Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="cassette-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Target className="w-8 h-8 text-[#E8934D]" />
                    <span className="text-3xl retro-title text-[#2C1810]">{hubStats.overall_stats.total_tests}</span>
                  </div>
                  <p className="text-[#6B4423] font-semibold">Tests Completed</p>
                  <p className="text-sm text-[#6B4423] mt-1">
                    {hubStats.overall_stats.passed_tests} passed
                  </p>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <BarChart3 className="w-8 h-8 text-[#E8934D]" />
                    <span className="text-3xl retro-title text-[#2C1810]">
                      {hubStats.overall_stats.avg_score}%
                    </span>
                  </div>
                  <p className="text-[#6B4423] font-semibold">Average Score</p>
                  <p className="text-sm text-[#6B4423] mt-1">
                    {hubStats.overall_stats.overall_pass_rate}% pass rate
                  </p>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-8 h-8 text-[#E8934D]" />
                    <span className="text-3xl retro-title text-[#2C1810]">
                      {hubStats.overall_stats.accuracy_rate}%
                    </span>
                  </div>
                  <p className="text-[#6B4423] font-semibold">Accuracy Rate</p>
                  <p className="text-sm text-[#6B4423] mt-1">
                    {hubStats.overall_stats.correct_questions}/{hubStats.overall_stats.total_questions} correct
                  </p>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Flame className="w-8 h-8 text-[#E85D4D]" />
                    <span className="text-3xl retro-title text-[#E85D4D]">
                      {hubStats.study_analytics.current_streak}
                    </span>
                  </div>
                  <p className="text-[#6B4423] font-semibold">Day Streak</p>
                  <p className="text-sm text-[#6B4423] mt-1">Keep it up!</p>
                </div>
              </div>

              {/* Category Performance */}
              {Object.keys(hubStats.category_stats).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-2xl retro-title mb-4 text-[#2C1810]">Category Performance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {Object.entries(hubStats.category_stats).map(([category, stats]) => (
                      <div key={category} className="cassette-card p-6">
                        <h4 className="text-xl retro-title text-[#2C1810] mb-3 capitalize">{category}</h4>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm text-[#6B4423]">Tests:</span>
                            <span className="font-bold text-[#E8934D]">{stats.total}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-[#6B4423]">Passed:</span>
                            <span className="font-bold text-[#4D9E7C]">{stats.passed}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-[#6B4423]">Avg Score:</span>
                            <span className={`font-bold ${stats.avg_score >= 70 ? 'text-[#4D9E7C]' : 'text-[#E85D4D]'}`}>
                              {stats.avg_score.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Study Activity */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="cassette-card p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-6 h-6 text-[#E8934D]" />
                    <h4 className="text-lg font-bold text-[#2C1810]">This Week</h4>
                  </div>
                  <p className="text-3xl retro-title text-[#E8934D]">
                    {hubStats.study_analytics.tests_this_week}
                  </p>
                  <p className="text-sm text-[#6B4423]">tests completed</p>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Calendar className="w-6 h-6 text-[#E8934D]" />
                    <h4 className="text-lg font-bold text-[#2C1810]">This Month</h4>
                  </div>
                  <p className="text-3xl retro-title text-[#E8934D]">
                    {hubStats.study_analytics.tests_this_month}
                  </p>
                  <p className="text-sm text-[#6B4423]">tests completed</p>
                </div>

                <div className="cassette-card p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <BookOpen className="w-6 h-6 text-[#E8934D]" />
                    <h4 className="text-lg font-bold text-[#2C1810]">Course Progress</h4>
                  </div>
                  <p className="text-3xl retro-title text-[#E8934D]">
                    {hubStats.progress.completion_rate}%
                  </p>
                  <p className="text-sm text-[#6B4423]">
                    {hubStats.progress.courses_completed}/{hubStats.progress.courses_enrolled} completed
                  </p>
                </div>
              </div>

              {/* Achievement Badges */}
              {hubStats.badges && hubStats.badges.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-2xl retro-title mb-4 text-[#2C1810]">Achievement Badges</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {hubStats.badges.map((badge, idx) => (
                      <div key={idx} className="cassette-card p-4 text-center hover:shadow-lg transition-shadow">
                        <div className="text-4xl mb-2">{badge.icon}</div>
                        <p className="font-bold text-[#2C1810] text-sm mb-1">{badge.name}</p>
                        <p className="text-xs text-[#6B4423]">{badge.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Test History */}
              {hubStats.recent_tests && hubStats.recent_tests.length > 0 && (
                <div>
                  <h3 className="text-2xl retro-title mb-4 text-[#2C1810]">Recent Tests</h3>
                  <div className="cassette-card p-6">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-[#E8B67A]">
                            <th className="text-left py-3 px-2 text-[#6B4423] font-semibold">Category</th>
                            <th className="text-left py-3 px-2 text-[#6B4423] font-semibold">Score</th>
                            <th className="text-left py-3 px-2 text-[#6B4423] font-semibold">Status</th>
                            <th className="text-left py-3 px-2 text-[#6B4423] font-semibold">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hubStats.recent_tests.slice(0, 5).map((test, idx) => (
                            <tr key={idx} className="border-b border-[#F4E4C1]">
                              <td className="py-3 px-2 text-[#2C1810] capitalize">{test.category || 'N/A'}</td>
                              <td className="py-3 px-2">
                                <span className={`font-bold ${test.score >= 70 ? 'text-[#4D9E7C]' : 'text-[#E85D4D]'}`}>
                                  {test.score}%
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                <span className={`px-2 py-1 rounded text-xs font-bold ${test.passed ? 'bg-[#4D9E7C] text-white' : 'bg-[#E85D4D] text-white'}`}>
                                  {test.passed ? 'PASSED' : 'FAILED'}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-[#6B4423] text-sm">
                                {test.completed_at ? new Date(test.completed_at).toLocaleDateString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            <div className="cassette-card p-6 text-center">
              <BookOpen className="w-12 h-12 text-[#E8934D] mx-auto mb-2" />
              <p className="text-3xl retro-title text-[#2C1810]">{dashboard.length}</p>
              <p className="text-[#6B4423]">Enrolled Courses</p>
            </div>
            
            <div className="cassette-card p-6 text-center">
              <Trophy className="w-12 h-12 text-[#E8934D] mx-auto mb-2" />
              <p className="text-3xl retro-title text-[#2C1810]">{certificates.length}</p>
              <p className="text-[#6B4423]">Certificates</p>
            </div>
            
            <div className="cassette-card p-6 text-center">
              <Award className="w-12 h-12 text-[#E8934D] mx-auto mb-2" />
              <p className="text-3xl retro-title text-[#2C1810]">{achievements.length}</p>
              <p className="text-[#6B4423]">Achievements</p>
            </div>
            
            <div className="cassette-card p-6 text-center">
              <Heart className="w-12 h-12 text-[#E8934D] mx-auto mb-2" />
              <p className="text-3xl retro-title text-[#2C1810]">{wishlist.length}</p>
              <p className="text-[#6B4423]">Wishlist</p>
            </div>
          </div>

          {/* Enrolled Courses */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-4xl retro-title">My Courses</h2>
              <button 
                disabled 
                className="retro-button-small opacity-50 cursor-not-allowed bg-gray-400"
                title="Coming Soon"
              >
                Browse Courses
              </button>
            </div>

            {dashboard.length === 0 ? (
              <div className="cassette-card p-12 text-center">
                <BookOpen className="w-16 h-16 text-[#6B4423] mx-auto mb-4" />
                <p className="text-[#3D2817] text-xl mb-4">No enrolled courses yet</p>
                <button 
                  disabled 
                  className="retro-button w-full sm:w-auto opacity-50 cursor-not-allowed bg-gray-400"
                  title="Coming Soon"
                >
                  Explore Courses
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {dashboard.map(({ course, progress_percentage, completed_items, total_items }) => (
                  <div key={course.id} className="cassette-card overflow-hidden group">
                    {course.cover_image_url && (
                      <div className="h-40 overflow-hidden">
                        <img
                          src={course.cover_image_url}
                          alt={course.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <div className="p-6">
                      <h3 className="text-xl retro-title text-[#2C1810] mb-2">{course.title}</h3>
                      <p className="text-sm text-[#6B4423] mb-4">By {course.author}</p>
                      
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#6B4423]">Progress</span>
                          <span className="font-bold text-[#E8934D]">{progress_percentage}%</span>
                        </div>
                        <div className="w-full h-3 bg-[#F4E4C1] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#E8934D] to-[#E85D4D]"
                            style={{ width: `${progress_percentage}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-[#6B4423] mt-1">
                          {completed_items} / {total_items} completed
                        </p>
                      </div>

                      <Link
                        to={`/courses/${course.id}/learn`}
                        className="retro-button w-full text-center block"
                      >
                        Continue Learning
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Achievements */}
          {achievements.length > 0 && (
            <div className="mb-12">
              <h2 className="text-4xl retro-title mb-6">Achievements</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {achievements.map((achievement) => (
                  <div key={achievement.id} className="cassette-card p-4 text-center">
                    <div className="text-4xl mb-2">{achievement.icon}</div>
                    <p className="text-sm font-bold text-[#2C1810]">{achievement.title}</p>
                    <p className="text-xs text-[#6B4423]">{achievement.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certificates */}
          {certificates.length > 0 && (
            <div className="mb-12">
              <h2 className="text-4xl retro-title mb-6">Certificates</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {certificates.map((cert) => (
                  <div key={cert.id} className="cassette-card p-6 border-4 border-[#E8934D]">
                    <div className="flex items-center gap-4 mb-4">
                      <Trophy className="w-12 h-12 text-[#E8934D]" />
                      <div>
                        <h3 className="text-xl retro-title text-[#2C1810]">{cert.course_title}</h3>
                        <p className="text-sm text-[#6B4423]">By {cert.course_author}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#6B4423] mb-2">
                      Certificate #: {cert.certificate_number}
                    </p>
                    <p className="text-sm text-[#6B4423]">
                      Issued: {new Date(cert.issued_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Publication Subscription Status */}
          {publicationMembership && publicationMembership.status === 'active' && (
            <div className="mb-12">
              <div className="cassette-card p-8 bg-gradient-to-br from-amber-100 to-yellow-100 border-4 border-amber-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <BookOpen className="w-12 h-12 text-amber-700" />
                    <div>
                      <h3 className="text-2xl retro-title text-[#2C1810] mb-2">Publication Center Membership</h3>
                      <p className="text-gray-700 font-semibold">
                        Status: <span className="text-green-600">Active</span>
                      </p>
                      {publicationMembership.cancel_at_period_end ? (
                        <p className="text-orange-600 font-semibold mt-1">
                          Subscription ends on {new Date(publicationMembership.current_period_end).toLocaleDateString()}
                        </p>
                      ) : (
                        <p className="text-gray-600 mt-1">
                          Renews {new Date(publicationMembership.current_period_end).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Link 
                      to="/my-publications" 
                      className="bg-amber-700 text-white px-6 py-2 rounded-lg font-bold hover:bg-amber-800 transition-all text-center"
                    >
                      My Publications
                    </Link>
                    {!publicationMembership.cancel_at_period_end && (
                      <button
                        onClick={async () => {
                          if (window.confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your current billing period.')) {
                            try {
                              await axios.post(`${API}/publication-subscription/cancel`, {}, { withCredentials: true });
                              toast.success('Subscription cancelled. Access continues until period end.');
                              loadDashboard();
                            } catch (error) {
                              toast.error('Failed to cancel subscription');
                            }
                          }
                        }}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-700 transition-all"
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Jeopardy Lifetime Score & Leaderboard */}
          <div className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* User's Lifetime Score */}
              <div className="cassette-card p-8 bg-gradient-to-br from-orange-100 to-yellow-100 border-4 border-orange-500">
                <div className="text-center">
                  <Trophy className="w-16 h-16 text-orange-600 mx-auto mb-4" />
                  <h3 className="text-2xl retro-title text-[#2C1810] mb-2">Your Jeopardy Stats</h3>
                  {jeopardyLifetimeScore ? (
                    <>
                      <div className="text-6xl font-bold text-orange-600 mb-2">
                        {jeopardyLifetimeScore.lifetime_score.toLocaleString()}
                      </div>
                      <p className="text-lg text-[#6B4423] mb-1">Lifetime Points</p>
                      <p className="text-sm text-[#6B4423]">
                        {jeopardyLifetimeScore.games_played} {jeopardyLifetimeScore.games_played === 1 ? 'Game' : 'Games'} Played
                      </p>
                      <Link 
                        to="/jeopardy-center" 
                        className="mt-4 inline-block bg-orange-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-orange-700 transition-all"
                      >
                        Play Jeopardy
                      </Link>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-600 mb-4">No games played yet</p>
                      <Link 
                        to="/jeopardy-center" 
                        className="inline-block bg-orange-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-orange-700 transition-all"
                      >
                        Start Playing
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {/* Top 5 Leaderboard */}
              <div className="cassette-card p-8">
                <h3 className="text-2xl retro-title text-[#2C1810] mb-6 text-center">🏆 Top 5 Legends 🏆</h3>
                {jeopardyLeaderboard.length > 0 ? (
                  <div className="space-y-3">
                    {jeopardyLeaderboard.map((player, idx) => {
                      const isCurrentUser = user && player.user_id === user.id;
                      const isLegend = idx === 0;
                      
                      return (
                        <div 
                          key={player.user_id} 
                          className={`p-4 rounded-lg flex items-center gap-4 ${
                            isLegend 
                              ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-4 border-yellow-600' 
                              : isCurrentUser 
                                ? 'bg-blue-100 border-2 border-blue-500' 
                                : 'bg-gray-100'
                          }`}
                        >
                          <div className={`text-3xl font-bold ${isLegend ? 'text-white' : 'text-gray-700'} w-12 text-center`}>
                            {idx === 0 ? '👑' : `#${idx + 1}`}
                          </div>
                          <div className="flex-1">
                            <div className={`font-bold ${isLegend ? 'text-xl' : 'text-lg'} ${isLegend ? 'text-white' : 'text-gray-800'}`}>
                              {player.username}
                              {isCurrentUser && <span className="ml-2 text-sm">(You)</span>}
                            </div>
                            {isLegend && (
                              <div className="text-sm font-bold text-white opacity-90">
                                ⭐ LEGEND OF THE INTERNET ⭐
                              </div>
                            )}
                            <div className={`text-sm ${isLegend ? 'text-white opacity-90' : 'text-gray-600'}`}>
                              {player.games_played} {player.games_played === 1 ? 'game' : 'games'} played
                            </div>
                          </div>
                          <div className={`text-right ${isLegend ? 'text-white' : 'text-orange-600'}`}>
                            <div className={`${isLegend ? 'text-3xl' : 'text-2xl'} font-bold`}>
                              {player.lifetime_score.toLocaleString()}
                            </div>
                            <div className={`text-xs ${isLegend ? 'text-white opacity-90' : 'text-gray-600'}`}>points</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-gray-600 py-8">
                    <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p>No players yet. Be the first Legend!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Test Progress by Category & Tier */}
          {Object.keys(categoryTierProgress).length > 0 && (
            <div className="mb-12">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-4xl retro-title">Test Progress Overview</h2>
                <Link to="/testing-center" className="retro-button-small">
                  Browse Tests
                </Link>
              </div>
              
              {/* Grid of categories (exclude herbs) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {['acupuncture', 'foundations', 'bio'].map(category => {
                  if (!categoryTierProgress[category]) return null;
                  const categoryData = categoryTierProgress[category];
                  
                  return (
                    <div key={category} className="cassette-card p-6">
                      <h3 className="text-2xl retro-title text-[#2C1810] mb-4 capitalize">{category}</h3>
                      
                      {Object.entries(categoryData).map(([tier, data]) => (
                        <div key={tier} className="mb-3 pb-3 border-b border-[#E8B67A] last:border-0">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-semibold text-[#6B4423]">
                              {tier === 'basic' ? 'Basic' : tier === 'comps_ready' ? 'Comps Ready' : 'Advanced'}
                            </span>
                            <span className="text-xs text-[#6B4423]">{data.total_tests} tests</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-[#6B4423]">Avg Score:</span>
                            <span className={`font-bold text-sm ${data.avg_score >= 70 ? 'text-[#4D9E7C]' : 'text-[#E85D4D]'}`}>
                              {data.avg_score.toFixed(1)}%
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-[#6B4423]">Total Attempts:</span>
                            <span className="font-bold text-sm text-[#E8934D]">{data.total_attempts}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Individual Test Progress */}
          {testProgress.length > 0 && (
            <div className="mb-12">
              <h2 className="text-3xl retro-title mb-6">Individual Test Results</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testProgress.map(({ test, best_score, total_attempts }) => (
                  <div key={test.id} className="cassette-card p-6">
                    <h3 className="text-xl retro-title text-[#2C1810] mb-2">{test.title}</h3>
                    <div className="flex gap-2 mb-4">
                      <span className="text-xs px-2 py-1 bg-[#E8934D] text-white rounded">
                        {test.tier === 'basic' ? 'Basic' : test.tier === 'comps_ready' ? 'Comps Ready' : 'Advanced'}
                      </span>
                      {test.category && (
                        <span className="text-xs px-2 py-1 bg-[#6B4423] text-white rounded capitalize">
                          {test.category}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between">
                        <span className="text-[#6B4423]">Best Score:</span>
                        <span className={`font-bold ${best_score >= test.passing_score ? 'text-[#4D9E7C]' : 'text-[#E85D4D]'}`}>
                          {best_score.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#6B4423]">Attempts:</span>
                        <span className="font-bold text-[#E8934D]">{total_attempts}</span>
                      </div>
                    </div>

                    <Link
                      to={`/tests/${test.id}`}
                      className="retro-button-small w-full text-center block"
                    >
                      Retake Test
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Wishlist */}
          {wishlist.length > 0 && (
            <div>
              <h2 className="text-4xl retro-title mb-6">Wishlist</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {wishlist.map((course) => (
                  <Link
                    key={course.id}
                    to={`/courses/${course.id}`}
                    className="cassette-card overflow-hidden group hover:scale-105 transition-transform"
                  >
                    {course.cover_image_url && (
                      <div className="h-32 overflow-hidden">
                        <img
                          src={course.cover_image_url}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="text-lg retro-title text-[#2C1810] mb-1">{course.title}</h3>
                      <p className="text-sm text-[#6B4423]">
                        {course.is_free ? 'FREE' : `$${course.price}`}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

        </div>
      </section>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cassette-card max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl retro-title flex items-center gap-2">
                <Lock className="w-8 h-8" />
                Account Settings
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-[#6B4423] hover:text-[#E85D4D] text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-[#6B4423] font-bold mb-2">Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                />
              </div>

              <div>
                <label className="block text-[#6B4423] font-bold mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                  minLength={6}
                />
                <p className="text-sm text-[#6B4423] mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-[#6B4423] font-bold mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#6B4423] rounded bg-[#F4E4C1] text-[#2C1810]"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="retro-button flex-1"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="retro-button bg-[#6B4423] hover:bg-[#3D2817] flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
